// ===========================================
// KITCHEN ORDERS HOOK
// ===========================================
// Uses WebSocket for real-time updates with polling fallback

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWebSocket, WebSocketMessage } from '@mise/shared';
import { kitchenApi } from '../services/api';
import { useKDSStore, Ticket } from '../stores/kdsStore';

const POLLING_INTERVAL = 10000; // 10 seconds fallback polling
const WS_URL = import.meta.env.VITE_WS_URL || null;
const WS_ENABLED = !!import.meta.env.VITE_WS_URL;

export function useKitchenOrders() {
  const { tickets, setTickets, selectedStation, settings } = useKDSStore();
  const queryClient = useQueryClient();
  const previousTicketIds = useRef<Set<string>>(new Set());
  const [useWebSocketMode, setUseWebSocketMode] = useState(true);

  // Get auth token from storage
  const getToken = useCallback(() => {
    // Try direct token first (set by auth store)
    const directToken = localStorage.getItem('kds_token');
    if (directToken) return directToken;

    // Fall back to zustand persisted state
    try {
      const auth = localStorage.getItem('kds-auth');
      if (auth) {
        const parsed = JSON.parse(auth);
        return parsed.state?.token || null;
      }
    } catch {
      return null;
    }
    return null;
  }, []);

  // Initialize audio context for new ticket alerts
  const audioContextRef = useRef<AudioContext | null>(null);

  // Play alert sound for new tickets using Web Audio API
  const playAlertSound = useCallback(() => {
    if (!settings.alertSoundEnabled) return;

    try {
      // Create audio context on demand (required for iOS)
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      // Create a pleasant two-tone notification sound
      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        // Envelope for smooth sound
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(0.2, startTime + duration * 0.5);
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = ctx.currentTime;
      // Two-tone "ding-dong" effect - attention-grabbing for kitchen
      playTone(880, now, 0.15);        // A5
      playTone(1108.73, now + 0.15, 0.25); // C#6
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, [settings.alertSoundEnabled]);

  // iOS blocks Web Audio until the AudioContext is unlocked inside a real user
  // gesture. New tickets arrive via WebSocket (not a tap), so without this the
  // beep silently fails on device. Prime the context on the first interaction.
  useEffect(() => {
    const unlock = () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') ctx.resume();
        // A 1-sample silent buffer fully unlocks audio playback on iOS.
        const source = ctx.createBufferSource();
        source.buffer = ctx.createBuffer(1, 1, 22050);
        source.connect(ctx.destination);
        source.start(0);
      } catch {
        // ignore — will retry on the next gesture
      }
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('touchend', unlock);
    };
    document.addEventListener('pointerdown', unlock, { passive: true });
    document.addEventListener('touchend', unlock, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('touchend', unlock);
    };
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    // Handle kitchen events
    if (message.type.startsWith('kitchen.')) {
      // Invalidate tickets query to refetch
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });

      // Play sound for new tickets
      if (message.type === 'kitchen.ticket_received') {
        playAlertSound();
      }
    }

    // Handle course events
    if (message.type.startsWith('course.')) {
      // Invalidate tickets query to refetch
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });

      // Play sound when course is fired (new items arriving)
      if (message.type === 'course.fired') {
        playAlertSound();
        console.log('[KDS] Course fired:', message.payload);
      }

      // Log course ready (expo station would handle pickup)
      if (message.type === 'course.ready') {
        console.log('[KDS] Course ready:', message.payload);
      }
    }

    // Handle order events that affect kitchen
    if (
      message.type === 'order.sent_to_kitchen' ||
      message.type === 'order.submitted' ||
      message.type === 'order.updated'
    ) {
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });

      // Play sound when order is sent to kitchen
      if (message.type === 'order.sent_to_kitchen') {
        playAlertSound();
      }
    }

    // Handle check transfer - refresh tickets to show updated table number
    if (message.type === 'check.transferred') {
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });
      console.log('[KDS] Check transferred:', message.payload);
    }
  }, [queryClient, playAlertSound]);

  // WebSocket connection - only if explicitly configured
  const {
    isConnected: wsConnected,
    error: wsError,
    subscribe,
  } = useWebSocket({
    url: WS_URL || '',
    token: WS_ENABLED ? getToken() : null,  // Pass null to prevent connection
    autoReconnect: WS_ENABLED,
    onMessage: handleWebSocketMessage,
    onConnected: () => {
      console.log('[KDS] WebSocket connected');
      setUseWebSocketMode(true);
      // Subscribe to kitchen channel
      subscribe('kitchen');
    },
    onDisconnected: () => {
      if (WS_ENABLED) {
        console.log('[KDS] WebSocket disconnected, falling back to polling');
      }
      setUseWebSocketMode(false);
    },
  });

  // Transform API response to Ticket format
  const transformTickets = useCallback((apiTickets: any[]): Ticket[] => {
    return apiTickets.map((ticket: any) => {
      const items = Object.entries(ticket.stationItems || {}).flatMap(([station, stationItems]: [string, any]) =>
        stationItems.map((item: any) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          modifiers: item.modifiers || [],
          specialInstructions: item.specialInstructions,
          allergenAlert: item.allergenAlert,
          station,
          status: item.status === 'ready' ? 'completed' : item.status === 'cooking' ? 'in_progress' : 'pending',
          startedAt: item.startedAt,
          completedAt: item.completedAt,
          seatNumber: item.seatNumber,
        }))
      );

      // Check for any allergen alerts
      const hasAllergenAlert = items.some((i: any) => i.allergenAlert) ||
        (ticket.specialInstructions && ticket.specialInstructions.includes('ALLERGY'));

      // Extract allergen info from notes if present
      const allergenMatch = ticket.specialInstructions?.match(/ALLERGY ALERT: ([^|]+)/);
      const allergenAlert = allergenMatch ? allergenMatch[1].trim() : undefined;

      return {
        id: ticket.id,
        orderId: ticket.id,
        tableNumber: ticket.tableNumber || 'N/A',
        serverName: ticket.serverName || 'Unknown',
        partySize: 0,
        items,
        createdAt: ticket.sentAt,
        sentAt: ticket.sentAt,
        status: ticket.status === 'ready' ? 'ready' : ticket.status === 'cooking' ? 'in_progress' : 'new',
        priority: ticket.isRush ? 'rush' : 'normal',
        courseNumber: ticket.courseNumber,
        notes: ticket.specialInstructions,
        hasAllergenAlert,
        allergenAlert,
      };
    });
  }, []);

  // Fetch tickets (polling or on-demand). ALWAYS unfiltered: the store
  // filters by station client-side, so every station's count stays correct —
  // and a stable dataset means switching stations can't false-fire the
  // new-ticket sound.
  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['kitchen-tickets'],
    queryFn: async () => {
      const response = await kitchenApi.getTickets(undefined);
      const rawTickets = response.data?.data || response.data || [];
      return transformTickets(rawTickets);
    },
    // Only poll when WebSocket is not connected
    refetchInterval: useWebSocketMode && wsConnected ? false : POLLING_INTERVAL,
    refetchIntervalInBackground: !wsConnected,
  });

  // Check for new tickets and play sound (for polling mode)
  useEffect(() => {
    if (data && !wsConnected) {
      const currentIds = new Set(data.map((t) => t.id));
      const previousIds = previousTicketIds.current;

      // Check for new tickets
      const newTickets = data.filter((t) => !previousIds.has(t.id));
      if (newTickets.length > 0 && previousIds.size > 0) {
        playAlertSound();
      }

      // Update previous IDs
      previousTicketIds.current = currentIds;
    }

    // Update store
    if (data) {
      setTickets(data);
    }
  }, [data, setTickets, playAlertSound, wsConnected]);

  return {
    tickets,
    isLoading,
    error: error || wsError,
    refetch,
    isRealtime: wsConnected,
    lastUpdatedAt: dataUpdatedAt,
  };
}

// Re-export for backwards compatibility
export { useKitchenOrders as useKitchenPolling };
